/** Helpers for the "Live Link" question type (URL template + clipboard copy). */

import {
  type BrowserHelperConfig,
  HERA_COURT_LINK_HASH_PARAM,
  HERA_COURT_LINK_PAYLOAD_VERSION,
  encodeCourtLinkHelperPayload,
  isBrowserHelperConfig,
  substituteBrowserHelperTemplate,
} from "@/lib/court-link-helper";

export type LiveLinkHttpMethod = "GET" | "POST" | "EXTENSION";

export type LiveLinkPlaceholderVars = Record<string, string>;

export class LiveLinkResolveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LiveLinkResolveError";
  }
}

export class LiveLinkFormatError extends LiveLinkResolveError {
  constructor() {
    super("Case number does not match the configured extract pattern.");
    this.name = "LiveLinkFormatError";
  }
}

/**
 * Apply an optional extract regex to the answer and return placeholder vars.
 *
 * Always includes `value` (raw answer). When a regex is set, capture groups
 * become `1`, `2`, … and `extracted` aliases group 1. Raises when the regex
 * is set but does not match.
 */
export function extractLiveLinkVars(
  value: string,
  valueExtractRegex: string | null | undefined,
  requestFields: Record<string, string> | null | undefined = null,
): LiveLinkPlaceholderVars {
  const vars: LiveLinkPlaceholderVars = { value };

  const pattern = valueExtractRegex?.trim();
  if (pattern) {
    let regex: RegExp;
    try {
      regex = new RegExp(pattern);
    } catch {
      throw new LiveLinkResolveError("Live Link extract regex is invalid.");
    }
    const match = regex.exec(value);
    if (!match) {
      throw new LiveLinkFormatError();
    }
    for (let i = 1; i < match.length; i += 1) {
      vars[String(i)] = match[i] ?? "";
    }
    if (match[1] != null) {
      vars.extracted = match[1];
    }
  }

  if (requestFields) {
    for (const [key, fieldTemplate] of Object.entries(requestFields)) {
      const name = key.trim();
      if (!name) {
        continue;
      }
      // Request-field templates may reference value / capture groups only.
      vars[name] = substituteLiveLinkPlaceholders(fieldTemplate, vars, {
        encodeValue: false,
      });
    }
  }

  return vars;
}

/**
 * Substitute ``{{name}}`` placeholders (optional surrounding whitespace).
 *
 * When ``encodeValue`` is true (URL templates), substituted values are
 * URL-encoded. Request-field templates leave values unencoded.
 *
 * Missing placeholders throw when ``requirePlaceholders`` is true so callers
 * do not open broken URLs like ``.../encrypt/---``.
 */
export function substituteLiveLinkPlaceholders(
  template: string,
  vars: LiveLinkPlaceholderVars,
  options: { encodeValue?: boolean; requirePlaceholders?: boolean } = {},
): string {
  const encodeValue = options.encodeValue ?? true;
  const requirePlaceholders = options.requirePlaceholders ?? false;
  const missing: string[] = [];
  const resolved = template.replace(
    /\{\{\s*([^{}]+?)\s*\}\}/g,
    (_match, rawName: string) => {
      const name = rawName.trim();
      if (!(name in vars)) {
        missing.push(name);
        return "";
      }
      const raw = vars[name] ?? "";
      return encodeValue ? encodeURIComponent(raw) : raw;
    },
  );
  if (requirePlaceholders && missing.length > 0) {
    const unique = [...new Set(missing)];
    throw new LiveLinkResolveError(
      `Live Link URL is missing values for ${unique
        .map((name) => `{{${name}}}`)
        .join(", ")}. Check the extract regex configuration.`,
    );
  }
  return resolved;
}

/**
 * Build the resolved href for a Live Link question answer.
 *
 * Supports ``{{value}}``, capture-group placeholders from
 * ``valueExtractRegex``, and named ``requestFields``.
 */
export function buildLiveLinkHref(
  urlTemplate: string | null | undefined,
  value: string,
  options: {
    valueExtractRegex?: string | null;
    requestFields?: Record<string, string> | null;
  } = {},
): string | null {
  const template = urlTemplate?.trim();
  if (!template) {
    return null;
  }
  const vars = extractLiveLinkVars(
    value,
    options.valueExtractRegex,
    options.requestFields,
  );
  return substituteLiveLinkPlaceholders(template, vars, {
    encodeValue: true,
    requirePlaceholders: true,
  });
}

function staticHttpUrl(value: string | null | undefined): string | null {
  const text = (value ?? "").trim();
  if (!text || text.includes("{{")) {
    return null;
  }
  const lowered = text.toLowerCase();
  if (lowered.startsWith("https://") || lowered.startsWith("http://")) {
    return text;
  }
  return null;
}

/**
 * Court records page when a case number cannot be inserted into the live link.
 *
 * A blank-answer URL wins. Otherwise a template that does not need a case
 * number is used. POST encrypt endpoints are skipped unless a blank-answer
 * URL is set.
 */
export function courtRecordsUrlFromLiveLink(config: {
  blankAnswerUrl?: string | null;
  urlTemplate?: string | null;
  httpMethod?: string | null;
  browserHelperConfig?: Record<string, unknown> | null;
}): string | null {
  const explicit = staticHttpUrl(config.blankAnswerUrl);
  if (explicit) {
    return explicit;
  }
  const method = String(config.httpMethod ?? "GET")
    .trim()
    .toUpperCase();
  if (method === "POST") {
    return null;
  }
  const template = staticHttpUrl(config.urlTemplate);
  if (template) {
    return template;
  }
  if (method === "EXTENSION") {
    const navigate = config.browserHelperConfig?.navigate_url_template;
    if (typeof navigate === "string") {
      return staticHttpUrl(navigate);
    }
  }
  return null;
}

/**
 * Build an EXTENSION live-link URL: open_url + `#heraCourtLink=<payload>`.
 *
 * The Hera Court Link browser helper reads the hash, writes sessionStorage,
 * then navigates to ``navigate_url_template``.
 */
export function buildLiveLinkExtensionHref(
  urlTemplate: string | null | undefined,
  value: string,
  options: {
    valueExtractRegex?: string | null;
    requestFields?: Record<string, string> | null;
    browserHelperConfig?: BrowserHelperConfig | null;
  } = {},
): string | null {
  const openUrl = buildLiveLinkHref(urlTemplate, value, {
    valueExtractRegex: options.valueExtractRegex,
    requestFields: options.requestFields,
  });
  if (!openUrl) {
    return null;
  }
  const config = options.browserHelperConfig;
  if (!isBrowserHelperConfig(config)) {
    throw new LiveLinkResolveError(
      "EXTENSION live links require a valid browser helper configuration.",
    );
  }
  const vars = extractLiveLinkVars(
    value,
    options.valueExtractRegex,
    options.requestFields,
  );
  const navigate = substituteLiveLinkPlaceholders(
    config.navigate_url_template,
    vars,
    { encodeValue: true, requirePlaceholders: true },
  );
  const writes = (config.session_storage ?? []).map((entry) => ({
    key: entry.key,
    value: substituteBrowserHelperTemplate(entry.template, vars),
  }));
  const formFills = (config.form_fills ?? []).map((entry) => ({
    selector: entry.selector,
    value: substituteBrowserHelperTemplate(entry.template, vars),
    ...(entry.strip_leading_zeros ? { strip_leading_zeros: true } : {}),
  }));
  const encoded = encodeCourtLinkHelperPayload({
    v: HERA_COURT_LINK_PAYLOAD_VERSION,
    writes,
    navigate,
    ...(formFills.length > 0 ? { form_fills: formFills } : {}),
    ...(config.submit_selector
      ? { submit_selector: config.submit_selector }
      : {}),
    ...(config.activate_selector
      ? { activate_selector: config.activate_selector }
      : {}),
    ...(Array.isArray(config.activate_selectors) &&
    config.activate_selectors.length > 0
      ? { activate_selectors: config.activate_selectors }
      : {}),
    ...(config.result_selector
      ? { result_selector: config.result_selector }
      : {}),
  });
  const url = new URL(openUrl);
  url.hash = `${HERA_COURT_LINK_HASH_PARAM}=${encoded}`;
  return url.toString();
}

/**
 * Derive the clerk search-results URL from an encrypt URL and ``qs`` token.
 */
export function buildLiveLinkSearchResultsUrl(
  encryptUrl: string,
  qs: string,
): string {
  const origin = new URL(encryptUrl).origin;
  const rawQs = qs.includes("%") ? decodeURIComponent(qs) : qs;
  return `${origin}/ocs/searchResults?qs=${encodeURIComponent(rawQs)}`;
}

/**
 * Resolve a POST Live Link: fetch encrypt JSON, then build the results URL.
 *
 * ``fetchJson`` should call the Hera proxy (avoids third-party CORS).
 */
export async function resolveLiveLinkPostHref(
  encryptUrl: string,
  fetchJson: (url: string) => Promise<Record<string, unknown>>,
): Promise<string> {
  const payload = await fetchJson(encryptUrl);
  const qs = payload.qs;
  if (typeof qs !== "string" || !qs.trim()) {
    const message =
      typeof payload.message === "string" && payload.message.trim()
        ? payload.message
        : "Case search did not return a results token.";
    throw new LiveLinkResolveError(message);
  }
  if (payload.success === false) {
    throw new LiveLinkResolveError(
      typeof payload.message === "string" && payload.message.trim()
        ? payload.message
        : "Case search failed.",
    );
  }
  return buildLiveLinkSearchResultsUrl(encryptUrl, qs);
}

/**
 * Copy text to the clipboard, falling back to a hidden textarea for browsers /
 * non-secure contexts where the async Clipboard API is unavailable.
 *
 * Must be invoked from within a user gesture (e.g. a click handler) so the copy
 * is permitted by the browser.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy approach below.
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
